/**
 * The allowance rule: age sets a target, a split turns it into pay.
 *
 *     Weekly Target  =  age × dollars_per_year_of_age
 *                       ├─ guaranteed_share  → base allowance, paid every period
 *                       └─ remainder         → expected earnings from Cleanify gold
 *
 * The coefficient is a parameter rather than a hard-coded `age / 2` because
 * "half your age per week" is a US convention of a particular decade, and a
 * schema that bakes it in cannot be re-defaulted later without rewriting
 * families' history. Storing `{ formula, version, dollarsPerYearOfAge }` means
 * changing the default changes only new rules.
 *
 * The split exists because families disagree, loudly, about whether allowance
 * should be tied to chores at all. 100% guaranteed is an unconditional
 * allowance; 0% is pure pay-for-work; the default sits between. Taking a side
 * here would cost half the audience for no product benefit.
 *
 * What this file deliberately does *not* do is touch the gold exchange rate.
 * That rate is family-scoped in Cleanify — one gold is worth the same to an
 * eight-year-old and a sixteen-year-old by construction — so per-child age
 * scaling cannot be expressed in it, and raising it every birthday would
 * revalue gold the child already saved.
 */

import { periodIndexAt, periodsBetween, periodStart, CADENCES } from './periods.js';
import { ageAt } from './age.js';

export const ALLOWANCE_RULE_VERSION = 'fortunaut-allowance-v1';

export const ALLOWANCE_DEFAULTS = Object.freeze({
  formula: 'per_year_of_age',
  version: ALLOWANCE_RULE_VERSION,
  /** $0.50 per year of age per week — the `age / 2` convention, as a parameter. */
  dollarsPerYearOfAge: 0.5,
  currency: 'USD',
  cadence: 'weekly',
  /** Share of the target paid unconditionally. The rest is earned. */
  guaranteedShare: 0.5,
  /**
   * Targets round to the nearest half-dollar. Linear output gives $3.50, $4.50,
   * $5.50 — fine in a ledger, awkward in coins, and the parent settles in coins.
   */
  roundingCents: 50,
  floorCents: 0,
  ceilingCents: 2000,
});

export const withDefaults = (rule = {}) => ({ ...ALLOWANCE_DEFAULTS, ...rule });

/**
 * The child's whole weekly income target at a given age.
 *
 * Rounded first, then clamped, so a ceiling means the ceiling rather than
 * "the ceiling, plus up to half a dollar".
 */
export function weeklyTargetCents(age, rule = ALLOWANCE_DEFAULTS) {
  const cfg = withDefaults(rule);
  const raw = Math.max(0, Number(age) || 0) * cfg.dollarsPerYearOfAge * 100;
  const step = cfg.roundingCents > 0 ? cfg.roundingCents : 1;
  const rounded = Math.round(raw / step) * step;
  return Math.min(cfg.ceilingCents, Math.max(cfg.floorCents, rounded));
}

/** The formula is weekly; other cadences scale so a year still totals the same. */
export function targetForCadence(weeklyCents, cadence = 'weekly') {
  const { perYear } = CADENCES[cadence] ?? CADENCES.weekly;
  return Math.round((weeklyCents * 52) / perYear);
}

export function splitTarget(targetCents, rule = ALLOWANCE_DEFAULTS) {
  const cfg = withDefaults(rule);
  const share = Math.min(1, Math.max(0, cfg.guaranteedShare));
  const baseCents = Math.round(targetCents * share);
  return { baseCents, expectedEarnedCents: targetCents - baseCents };
}

/** Everything the formula produced for one child at one moment. */
export function allowanceAt(date, child, rule = ALLOWANCE_DEFAULTS) {
  const cfg = withDefaults(rule);
  const age = ageAt(date, child);
  const weeklyCents = weeklyTargetCents(age, cfg);
  const targetCents = targetForCadence(weeklyCents, cfg.cadence);
  return { age, weeklyTargetCents: weeklyCents, targetCents, ...splitTarget(targetCents, cfg) };
}

export const allowanceEntryId = (childId, rule, periodIndex) =>
  `allowance:${childId}:${withDefaults(rule).version}:${withDefaults(rule).cadence}:${periodIndex}`;

/**
 * Every allowance entry owed between two dates that isn't already recorded.
 *
 * This is the whole point of deriving periods. Call it on every app open, twice
 * concurrently, or once after a month offline — the entry IDs are a function of
 * (child, rule version, cadence, period index), so the second call finds nothing
 * to do and a month offline produces exactly the four entries it should.
 *
 * Each entry is priced by the child's age at the *start of its own period*, and
 * carries that age in its snapshot. A catch-up run six weeks late must pay what
 * was owed then; without the snapshot, a birthday in the gap would quietly
 * back-pay the raise to periods that predate it.
 */
export function dueAllowanceEntries({
  childId,
  child,
  rule = ALLOWANCE_DEFAULTS,
  from,
  to,
  existingIds = new Set(),
}) {
  const cfg = withDefaults(rule);
  const seen = existingIds instanceof Set ? existingIds : new Set(existingIds);

  return periodsBetween(from, to, cfg.cadence).flatMap((periodIndex) => {
    const id = allowanceEntryId(childId, cfg, periodIndex);
    if (seen.has(id)) return [];

    const occurredAt = periodStart(periodIndex, cfg.cadence);
    const { age, targetCents, baseCents, expectedEarnedCents } = allowanceAt(occurredAt, child, cfg);
    if (baseCents <= 0) return [];

    return [{
      id,
      childId,
      currency: 'usd',
      amountCents: baseCents,
      reasonType: 'allowance',
      periodIndex,
      occurredAt,
      snapshot: {
        age,
        targetCents,
        expectedEarnedCents,
        dollarsPerYearOfAge: cfg.dollarsPerYearOfAge,
        guaranteedShare: cfg.guaranteedShare,
        roundingCents: cfg.roundingCents,
        ruleVersion: cfg.version,
      },
    }];
  });
}

/**
 * The raise, as an event worth showing rather than a number that quietly moves.
 *
 * Returns null when nothing changed. When something did, this is the app's best
 * teachable moment: a child who *notices* the raise is a child you can ask what
 * happens if they save it instead.
 */
export function raiseBetween(beforeDate, afterDate, child, rule = ALLOWANCE_DEFAULTS) {
  const before = allowanceAt(beforeDate, child, rule);
  const after = allowanceAt(afterDate, child, rule);
  if (after.targetCents === before.targetCents) return null;
  return {
    fromAge: before.age,
    toAge: after.age,
    fromTargetCents: before.targetCents,
    toTargetCents: after.targetCents,
    deltaCents: after.targetCents - before.targetCents,
    baseDeltaCents: after.baseCents - before.baseCents,
    effectiveFrom: periodStart(periodIndexAt(afterDate, withDefaults(rule).cadence), withDefaults(rule).cadence),
  };
}
